import {t} from 'i18next';
import React, {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {isEmpty, parseQuery, unwrapQueryText} from '../../../utils/utils';
import AurButton from '../../common/aur-button/AurButton';
import AurDialog, {DialogProps} from '../../common/aur-dialog/AurDialog';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import {
  convertBetweenInRuleGroup,
  jsonLogicOperatorMap,
} from '../../../utils/queryBuilder';
import {formatQuery, transformQuery} from 'react-querybuilder';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_EVENT_NAMES,
  GA_CLICK_TYPES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';

export enum ClearConfirmPopupType {
  leaveSearchConfirm,
  confirmClearSearch,
  buildWithWizard,
}

interface Props extends DialogProps {
  queryText?: string;
  popupType: ClearConfirmPopupType;
  showCopyToClipboard?: boolean;
}

const ClearConfirmAlertPopup: React.FC<Props> = (props: Props) => {
  const {
    queryText,
    popupType,
    showCopyToClipboard = true,
    popupContent,
    ...rest
  } = props;

  const {trackGAEvent} = useAnalytics();

  const {builder} = useSelector((state: RootState) => state.query);

  const [copyClipboardLabel, setCopyClipboardLabel] = useState<string>(
    t('indexSearchPage.clearConfirmDialog.common.copyToClipboard')
  );

  const onClickCopyClipboard = useCallback(() => {
    let text = null;
    if (isEmpty(builder.rules)) {
      text = queryText;
    } else {
      const converted = convertBetweenInRuleGroup(builder);
      const jsonLogic = formatQuery(
        transformQuery(converted, {operatorMap: {...jsonLogicOperatorMap}}),
        'jsonlogic'
      );
      const result = parseQuery(jsonLogic);
      text = unwrapQueryText(result);
    }
    if (text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopyClipboardLabel(
            t('indexSearchPage.clearConfirmDialog.common.copied')
          );
          setTimeout(() => {
            setCopyClipboardLabel(
              t('indexSearchPage.clearConfirmDialog.common.copyToClipboard')
            );
          }, 2000);
        })
        .catch((_) => null);
    }
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.queryBuilder.copyToClipboard'),
        type: GA_CLICK_TYPES.LINK,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.queryBuilder.copySyntaxAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.global.unsavedChangesSubpageName'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  }, [builder.rules, queryText]);

  const renderCopyClipboardButton = useCallback(() => {
    return (
      <AurButton
        dataTestId={'copyToClipboardButton'}
        classNames={'clear-confim-alert-copy-text-button'}
        startIcon={<AurIcon icon={IconNames.ContentCopyOutlined} />}
        label={copyClipboardLabel}
        type={'text'}
        onClick={onClickCopyClipboard}
        border={'none'}
      />
    );
  }, [copyClipboardLabel, onClickCopyClipboard]);

  const getDialogPropsByType = useCallback(() => {
    let dialogProps: Pick<
      Props,
      | 'dialogTitle'
      | 'popupTitle'
      | 'popupContent'
      | 'labelActionCancel'
      | 'labelActionOk'
      | 'cancleLabelClassName'
      | 'showCopyToClipboard'
      | 'dismissable'
    >;

    switch (popupType) {
      case ClearConfirmPopupType.confirmClearSearch: {
        dialogProps = {
          dialogTitle: t(
            'indexSearchPage.clearConfirmDialog.confirmClearSearch.justToConfirm'
          ),
          popupTitle: t(
            'indexSearchPage.clearConfirmDialog.confirmClearSearch.areYouSureYouWantToClearFilters'
          ),
          popupContent: t(
            'indexSearchPage.clearConfirmDialog.confirmClearSearch.byClearingYouWillLoseSearchInputs'
          ),
          labelActionCancel: t(
            'indexSearchPage.clearConfirmDialog.common.cancel'
          ),
          labelActionOk: t(
            'indexSearchPage.clearConfirmDialog.confirmClearSearch.yesClearAll'
          ),
          showCopyToClipboard: false,
        };
        break;
      }

      case ClearConfirmPopupType.leaveSearchConfirm: {
        dialogProps = {
          dialogTitle: t(
            'indexSearchPage.clearConfirmDialog.common.unsavedChanges'
          ),
          popupTitle: t(
            'indexSearchPage.clearConfirmDialog.leaveSearchConfirm.description'
          ),
          popupContent: renderCopyClipboardButton(),
          labelActionCancel: t(
            'indexSearchPage.clearConfirmDialog.leaveSearchConfirm.leaveWithoutSaving'
          ),
          labelActionOk: t(
            'indexSearchPage.clearConfirmDialog.leaveSearchConfirm.stayOnPage'
          ),
          cancleLabelClassName: 'clear-confim-alert-cancel-button',
          showCopyToClipboard: true,
          dismissable: false,
        };
        break;
      }

      case ClearConfirmPopupType.buildWithWizard: {
        dialogProps = {
          dialogTitle: t(
            'indexSearchPage.clearConfirmDialog.common.unsavedChanges'
          ),
          popupTitle: t(
            'indexSearchPage.clearConfirmDialog.buildWithWizard.description'
          ),
          popupContent: renderCopyClipboardButton(),
          labelActionCancel: t(
            'indexSearchPage.clearConfirmDialog.common.cancel'
          ),
          labelActionOk: t(
            'indexSearchPage.clearConfirmDialog.buildWithWizard.buildWithWizard'
          ),
          showCopyToClipboard: true,
          dismissable: false,
        };
        break;
      }
    }

    return dialogProps;
  }, [popupType, renderCopyClipboardButton]);

  return (
    <AurDialog
      className={'clear-confim-alert-cancel-dialog'}
      popupContent={
        showCopyToClipboard ? renderCopyClipboardButton() : popupContent
      }
      {...getDialogPropsByType()}
      {...rest}
    />
  );
};

export default ClearConfirmAlertPopup;
